import { CommonModule } from '@angular/common'
import { Component, inject, input } from '@angular/core'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { RouterModule } from '@angular/router'
import { Observable } from 'rxjs'
import { AssetService } from 'src/app/core/services/asset.service'
import { SearchComponent } from 'src/app/components/search/search.component'
import { IAssetCategoriesResponse } from 'src/app/core/interfaces/asset.interface'
import { ButtonModule } from '../button/button.module'
import { MediaLibraryService } from 'src/app/core/services/media-library.service'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { RoutePaths } from 'src/app/core/data/route-paths.data'
import { I18nService } from 'src/app/core/services/i18n.service'
import { SearchRoutePaths } from 'src/app/core/data/search-route-paths.data'
import { EMPTY_MEDIA_DATA, ImageSizes, IMediaData } from 'src/app/core/interfaces/media-library.interface'
import { I18nText } from 'src/app/core/interfaces/i18n.service'


export type HeroBannerSize = 'large' | 'medium' | 'small' | 'aspect-ratio'

@Component({
  selector: 'app-hero-banner',
  templateUrl: './hero-banner.component.html',
  styleUrls: ['./hero-banner.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatProgressSpinnerModule,
    SearchComponent,
    ButtonModule,
  ],
})
export class HeroBannerComponent {
  private _assetService = inject(AssetService)
  private _mediaLibraryService = inject(MediaLibraryService)
  protected i18n = inject(I18nService)

  enabled = input(false)
  styles = input<any>({})
  quantity = input(5, {
    transform: (value: any) => parseInt(value, 10),
  })
  imageToggle = input(false)
  imageSrcData = input<IMediaData>(EMPTY_MEDIA_DATA)
  bannerSize = input<HeroBannerSize>('large')
  bannerAltText = input<I18nText | string>('')
  mainText = input<I18nText | string>('')
  mainTextSize = input('h3')
  subText = input<I18nText | string>('')
  ctaText = input<I18nText | string>('')

  RoutePaths = RoutePaths
  SearchRoutePaths = SearchRoutePaths
  categories$!: Observable<IAssetCategoriesResponse>
  imageSrc: string = ''
  imageSrcSet: string = ''
  imageSizes: string = ''

  pillAriaLabel = $localize`:@@hero-banner.pill.aria-label:Link to view {category} results`

  constructor() {
    toObservable(this.quantity).pipe(
      takeUntilDestroyed(),
    ).subscribe(() => {
      this.loadCategories()
    })
    toObservable(this.imageSrcData).pipe(
      takeUntilDestroyed(),
    ).subscribe(() => {
      this.setImageSrc()
    })
  }

  loadCategories(): void {
    this.categories$ = this._assetService.getCategories({
      field: 'quantity',
      direction: 'desc',
      index: 0,
      size: this.quantity(),
    }, [
      'category',
      'image',
      'list_price',
      'quantity',
    ])
  }

  setImageSrc(): void {
    if (!this.imageSrcData()) {
      this.imageSrc = ''
      return
    }
    this.imageSrc = this.getImageSizeUrl()
    this.imageSrcSet = this._mediaLibraryService.getImageSrcSet(this.imageSrcData())
    this.imageSizes = this._mediaLibraryService.getImageSizes(this.imageSrcData())
  }

  getImageSizeUrl(): string {
    switch (this.bannerSize()) {
      case 'aspect-ratio':
        return this.imageSrcData().urls[ImageSizes.HeroBannerAspectRatio]
      case 'small':
        return this.imageSrcData().urls[ImageSizes.HeroBannerSmall]
      case 'medium':
        return this.imageSrcData().urls[ImageSizes.HeroBannerMedium]
      case 'large':
        return this.imageSrcData().urls[ImageSizes.HeroBannerLarge]
      default:
        return this.imageSrcData().urls[ImageSizes.Full]
    }
  }
}
